import { useState, useEffect } from "react";
import { fetchWithCache } from "../../../utils/apiCache";
import { API_ENDPOINTS } from "../../../config/api";
import Button from "../../../components/base/Button";
import Card from "../../../components/base/Card";
import type {
  PdfProject,
  VideoProject,
  Rendering360Project,
  ViewDirection,
} from "../../../mocks/portfolio";

export default function HomePortfolioSection() {
  const [serviceType, setServiceType] = useState<"drafting" | "takeoff">("drafting");
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [selectedPdf, setSelectedPdf] = useState<PdfProject | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoProject | null>(null);
  const [selected360, setSelected360] = useState<Rendering360Project | null>(null);
  const [active360Tab, setActive360Tab] = useState<ViewDirection>("south");

  const [activeProjects, setActiveProjects] = useState<any[]>([]);
  const [activeCategories, setActiveCategories] = useState<string[]>(["All"]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPortfolio = async () => {
      setLoading(true);
      try {
        const data = await fetchWithCache(API_ENDPOINTS.PORTFOLIO(serviceType));
        
        setActiveProjects(data || []);
        
        // Extract unique categories, ensuring "All" is first
        const uniqueCategories = ["All", ...Array.from(new Set((data || []).map((item: any) => item.category)))];
        setActiveCategories(uniqueCategories as string[]);
        
        // Reset filter
        setActiveFilter("All");
      } catch (error) {
        console.error(`Failed to fetch ${serviceType} portfolio`, error);
        setActiveProjects([]);
        setActiveCategories(["All"]);
      } finally {
        setLoading(false);
      }
    };
    fetchPortfolio();
  }, [serviceType]);

  const filteredProjects =
    activeFilter === "All"
      ? activeProjects
      : activeProjects.filter((p: any) => p.category === activeFilter);

  // Limit to 8 items for the homepage
  const displayedProjects = filteredProjects.slice(0, 8);

  const closeAllModals = () => {
    setSelectedProject(null);
    setSelectedPdf(null);
    setSelectedVideo(null);
    setSelected360(null);
  };

  const switchServiceType = (type: "drafting" | "takeoff") => {
    setServiceType(type);
    setActiveFilter("All");
    setSelectedProject(null);
    setSelectedPdf(null);
    setSelectedVideo(null);
    setSelected360(null);
  };

  const open360 = (project: Rendering360Project) => {
    setActive360Tab("south");
    setSelected360(project);
  };

  const viewLabels: { key: ViewDirection; label: string; icon: string }[] = [
    { key: "south", label: "South View", icon: "ri-arrow-down-line" },
    { key: "north", label: "North View", icon: "ri-arrow-up-line" },
    { key: "west", label: "West View", icon: "ri-arrow-left-line" },
    { key: "east", label: "East View", icon: "ri-arrow-right-line" },
  ];

  return (
    <>
      {/* ── Drafting / Takeoff Toggle ─────────────────────────────────────── */}
      <section id="portfolio" className="py-16 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-4">
              Portfolio Showcase
            </h2>
            <p className="text-[#889d8f] dark:text-gray-400 text-lg max-w-2xl mx-auto">
              Explore our latest projects and architectural renderings
            </p>
          </div>

          {/* Toggle Pill */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-gray-50 dark:bg-slate-800 rounded-2xl p-2 shadow-sm border border-gray-100 dark:border-slate-700 relative group">
              <button
                onClick={() => switchServiceType("drafting")}
                className={`relative flex items-center gap-2.5 px-6 py-2.5 sm:px-8 sm:py-3 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer group/btn ${serviceType === "drafting"
                  ? "bg-[#e18033] text-white shadow-lg scale-[1.02]"
                  : "text-[#889d8f] dark:text-gray-400 hover:text-[#e18033] dark:hover:text-[#e18033] hover:bg-orange-50/50 dark:hover:bg-orange-500/10"
                  }`}
              >
                <i className={`ri-draft-line text-base transition-transform duration-300 ${serviceType !== "drafting" && "group-hover/btn:scale-110"}`} />
                Drafting
                {serviceType === "drafting" && !loading && (
                  <span className="ml-1 px-2 py-0.5 bg-white/25 rounded-full text-xs font-bold">
                    {activeProjects.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => switchServiceType("takeoff")}
                className={`relative flex items-center gap-2.5 px-6 py-2.5 sm:px-8 sm:py-3 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer group/btn ${serviceType === "takeoff"
                  ? "bg-[#0d9488] text-white shadow-lg scale-[1.02]"
                  : "text-[#889d8f] dark:text-gray-400 hover:text-[#0d9488] dark:hover:text-[#0d9488] hover:bg-teal-50/50 dark:hover:bg-teal-500/10"
                  }`}
              >
                <i className={`ri-calculator-line text-base transition-transform duration-300 ${serviceType !== "takeoff" && "group-hover/btn:scale-110"}`} />
                Takeoff
                {serviceType === "takeoff" && !loading && (
                  <span className="ml-1 px-2 py-0.5 bg-white/25 rounded-full text-xs font-bold">
                    {activeProjects.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-2">
            {activeCategories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveFilter(category)}
                className={`px-4 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap text-sm ${activeFilter === category
                  ? serviceType === "drafting"
                    ? "bg-[#e18033] text-white shadow-lg"
                    : "bg-[#0d9488] text-white shadow-lg"
                  : "bg-gray-50 dark:bg-slate-800/50 text-[#212026] dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700/50 border border-gray-200 dark:border-slate-700"
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Portfolio Grid ────────────────────────────────────────────────── */}
      <section className="relative pt-16 pb-24 overflow-hidden bg-white dark:bg-slate-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse bg-gray-100 dark:bg-slate-800 rounded-2xl h-64 shadow-sm w-full"></div>
              ))
            ) : displayedProjects.map((project: any) => {
              // ── PDF Card ──────────────────────────────────────────────
              if (project.type === "pdf") {
                return (
                  <Card
                    key={project.id}
                    className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300 flex flex-col h-full"
                    onClick={() => setSelectedPdf(project)}
                  >
                    <div className="relative flex-1 min-h-[16rem] w-full bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                      {project.thumbnail || project.image ? (
                        <img
                          src={project.thumbnail || project.image}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                          alt={project.title}
                        />
                      ) : (
                        <div className="w-full h-full overflow-hidden group-hover:scale-110 transition-transform duration-500 relative bg-white">
                          <div className="absolute inset-0 z-10" />
                          <iframe
                            src={`${project.pdfUrl || project.url}#page=1&view=FitH&scrollbar=0&toolbar=0&navpanes=0`}
                            className="w-[150%] h-[150%] -ml-[25%] -mt-[25%] pointer-events-none"
                            style={{ border: 'none' }}
                            title={project.title}
                          />
                        </div>
                      )}
                      {/* Dark overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {/* PDF Badge */}
                      <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full shadow-lg">
                        <i className="ri-file-pdf-line text-sm" />
                        PDF
                      </div>
                      {/* Category badge */}
                      <div className="absolute top-4 right-4 px-2 py-1 bg-[#212026]/70 text-white text-xs rounded-full">
                        {project.category}
                      </div>
                      {/* Hover CTA */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="text-center text-white">
                          <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-2">
                            <i className="ri-file-pdf-line text-xl" />
                          </div>
                          <p className="text-sm font-medium">Open PDF</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              }

              // ── 360° Rendering Card ───────────────────────────────────
              if (project.type === "rendering360") {
                return (
                  <Card
                    key={project.id}
                    className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300 flex flex-col h-full"
                    onClick={() => open360(project)}
                  >
                    <div className="relative flex-1 min-h-[16rem] w-full bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                      <img
                        src={project.image}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        alt={project.title}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {/* 360° Badge */}
                      <div className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1.5 bg-[#e18033] text-white text-xs font-extrabold rounded-full shadow-lg tracking-wider">
                        <i className="ri-360-line text-sm" />
                        360°
                      </div>
                      {/* Category */}
                      <div className="absolute top-4 left-4 px-3 py-1 bg-[#212026]/70 text-white text-xs font-medium rounded-full">
                        {project.category}
                      </div>
                      {/* Hover CTA */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="text-center text-white">
                          <div className="w-12 h-12 bg-[#e18033] rounded-full flex items-center justify-center mx-auto mb-2">
                            <i className="ri-360-line text-xl" />
                          </div>
                          <p className="text-sm font-medium">View All Angles</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              }

              // ── Video Card ────────────────────────────────────────────
              if (project.type === "video") {
                return (
                  <Card
                    key={project.id}
                    className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300 flex flex-col h-full"
                    onClick={() => setSelectedVideo(project)}
                  >
                    <div className="relative flex-1 min-h-[16rem] w-full bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                      {project.thumbnail || project.image ? (
                        <img
                          src={project.thumbnail || project.image}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                          alt={project.title}
                        />
                      ) : (
                        <video
                          src={`${project.videoUrl || project.url}#t=0.1`}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                          preload="metadata"
                          muted
                          playsInline
                        />
                      )}
                      {/* Dark overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {/* Video Badge */}
                      <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 bg-violet-600 text-white text-xs font-bold rounded-full shadow-lg">
                        <i className="ri-play-circle-line text-sm" />
                        VIDEO
                      </div>
                      {/* Category badge */}
                      <div className="absolute top-4 right-4 px-2 py-1 bg-[#212026]/70 text-white text-xs rounded-full">
                        {project.category}
                      </div>
                      {/* Play button hover */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="text-center text-white">
                          <div className="w-14 h-14 bg-violet-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-xl">
                            <i className="ri-play-fill text-2xl ml-0.5" />
                          </div>
                          <p className="text-sm font-medium">Play Video</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              }

              // ── Standard Image Card ───────────────────────────────────
              return (
                <Card
                  key={project.id}
                  className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300"
                  onClick={() =>
                    setSelectedProject(
                      selectedProject === project.id ? null : project.id
                    )
                  }
                >
                  <div className="relative h-64 bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                    <img
                      src={project.image}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      alt={project.title}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="text-center text-white">
                        <div className="w-12 h-12 bg-[#e18033] rounded-full flex items-center justify-center mx-auto mb-2">
                          <i className="ri-eye-line text-xl" />
                        </div>
                        <p className="text-sm font-medium">View Project</p>
                      </div>
                    </div>
                    <div className="absolute top-4 left-4 px-3 py-1 bg-[#e18033] text-white text-xs font-medium rounded-full">
                      {project.category}
                    </div>
                    {/* Disclaimer badge with tooltip */}
                    {project.disclaimer && (
                      <div className="absolute bottom-3 right-3 group/disclaimer z-10" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/90 text-white text-[11px] font-semibold rounded-full cursor-help shadow-md backdrop-blur-sm">
                          <i className="ri-error-warning-line text-xs" />
                          {project.disclaimer.label}
                        </div>
                        {/* Tooltip */}
                        <div className="absolute bottom-full right-0 mb-2 w-60 px-3 py-2.5 bg-[#212026] text-white text-xs leading-relaxed rounded-xl shadow-xl opacity-0 group-hover/disclaimer:opacity-100 pointer-events-none transition-opacity duration-200 z-20">
                          {project.disclaimer.description}
                          <div className="absolute top-full right-4 w-2.5 h-2.5 bg-[#212026] rotate-45 -mt-1.5" />
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Image modal inline (or handled globally) */}
                  {selectedProject === project.id && (
                    <div
                      className="fixed inset-0 bg-[#212026]/80 dark:bg-black/90 flex items-center justify-center z-50 p-4"
                      onClick={closeAllModals}
                    >
                      <div
                        className="bg-white dark:bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="relative">
                          <img
                            src={project.image}
                            className="w-full h-full object-cover object-top rounded-t-2xl"
                            alt={project.title}
                          />
                          <button
                            onClick={closeAllModals}
                            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-[#e18033]/80 rounded-full flex items-center justify-center cursor-pointer transition-colors duration-300"
                          >
                            <i className="ri-close-line text-white text-xl" />
                          </button>
                          <div className="absolute top-4 left-4 px-3 py-1 bg-[#e18033] text-white text-sm font-medium rounded-full shadow-lg">
                            {project.category}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* ── Orange Gradient & CTA Overlay ────────────────────────────────── */}
        {filteredProjects.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-72 bg-gradient-to-t from-white via-white/90 to-transparent dark:from-slate-900 dark:via-slate-900/90 flex flex-col items-center justify-end pb-12 pointer-events-none z-10">
            {/* Orange glowing orb in the center behind the button */}
            <div className="absolute bottom-[-100px] left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#e18033]/15 dark:bg-[#e18033]/20 blur-[100px] rounded-full pointer-events-none" />
            
            <div className="text-center pointer-events-auto relative z-20">
              <h3 className="text-[#212026] dark:text-gray-100 text-xl md:text-2xl font-bold mb-6 drop-shadow-sm">
                Discover more of our past projects
              </h3>
              <a href="/portfolio">
                <Button
                  variant="outline"
                  size="lg"
                  className="mx-auto border-[#e18033] text-[#e18033] hover:bg-[#e18033] hover:text-white dark:hover:bg-[#e18033] dark:hover:text-white bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm transition-all shadow-lg hover:shadow-[#e18033]/25"
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-gallery-line" />
                  </div>
                  Explore Full Portfolio
                </Button>
              </a>
            </div>
          </div>
        )}
      </section>

      {/* ── Video Popup Modal ─────────────────────────────────────────────── */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-4xl flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-violet-600 rounded-lg flex items-center justify-center">
                  <i className="ri-play-circle-line text-white text-lg" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#212026] dark:text-gray-100 text-base">
                    {selectedVideo.title}
                  </h3>
                  <p className="text-xs text-[#889d8f] dark:text-gray-400">
                    {selectedVideo.category}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={selectedVideo.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-[#e18033] text-white text-sm font-medium rounded-lg hover:bg-[#c96a1a] transition-colors flex items-center gap-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="ri-external-link-line" />
                  Open in New Tab
                </a>
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="w-9 h-9 bg-gray-100 dark:bg-slate-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-[#212026] dark:text-gray-200 text-lg" />
                </button>
              </div>
            </div>
            {/* Video Player */}
            <div className="bg-black">
              <video
                key={selectedVideo.videoUrl}
                src={selectedVideo.videoUrl}
                controls
                autoPlay
                className="w-full max-h-[70vh] outline-none"
                style={{ display: "block" }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}

      {/* ── PDF Popup Modal ───────────────────────────────────────────────── */}
      {selectedPdf && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedPdf(null)}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-red-600 rounded-lg flex items-center justify-center">
                  <i className="ri-file-pdf-line text-white text-lg" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#212026] dark:text-gray-100 text-base">
                    {selectedPdf.title}
                  </h3>
                  <p className="text-xs text-[#889d8f] dark:text-gray-400">
                    {selectedPdf.category}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={selectedPdf.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-[#e18033] text-white text-sm font-medium rounded-lg hover:bg-[#c96a1a] transition-colors flex items-center gap-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="ri-external-link-line" />
                  Open in New Tab
                </a>
                <button
                  onClick={() => setSelectedPdf(null)}
                  className="w-9 h-9 bg-gray-100 dark:bg-slate-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-[#212026] dark:text-gray-200 text-lg" />
                </button>
              </div>
            </div>
            {/* PDF iframe */}
            <div className="flex-1 bg-gray-100 dark:bg-slate-900">
              <iframe
                src={selectedPdf.pdfUrl}
                className="w-full h-full border-0"
                title={selectedPdf.title}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── 360° Rendering Popup Modal ────────────────────────────────────── */}
      {selected360 && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4"
          onClick={() => setSelected360(null)}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#e18033] rounded-lg flex items-center justify-center">
                  <i className="ri-360-line text-white text-lg" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#212026] dark:text-gray-100 text-base">
                    {selected360.title}
                  </h3>
                  <p className="text-xs text-[#889d8f] dark:text-gray-400">
                    360° Rendering — All Directional Views
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelected360(null)}
                className="w-9 h-9 bg-gray-100 dark:bg-slate-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-[#212026] dark:text-gray-200 text-lg" />
              </button>
            </div>

            {/* Tab Bar */}
            <div className="flex border-b border-gray-200 dark:border-slate-700 flex-shrink-0 bg-gray-50 dark:bg-slate-900/50">
              {viewLabels.map((v) => (
                <button
                  key={v.key}
                  onClick={() => setActive360Tab(v.key)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition-all duration-200 cursor-pointer border-b-2 ${active360Tab === v.key
                    ? "border-[#e18033] text-[#e18033] bg-white dark:bg-slate-800"
                    : "border-transparent text-[#889d8f] dark:text-gray-400 hover:text-[#212026] dark:hover:text-gray-200 hover:bg-white/60 dark:hover:bg-slate-700/40"
                    }`}
                >
                  <i className={`${v.icon} text-base`} />
                  <span className="hidden sm:inline">{v.label}</span>
                  <span className="sm:hidden capitalize">{v.key}</span>
                </button>
              ))}
            </div>

            {/* Rendering Image */}
            <div className="flex-1 overflow-hidden bg-[#212026] relative min-h-0">
              <img
                key={active360Tab}
                src={selected360.views[active360Tab]}
                alt={`${active360Tab} view`}
                className="w-full h-full object-contain animate-fade-in"
                style={{ animation: "fadeIn 0.3s ease-in-out" }}
              />
              {/* Direction label overlay */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/60 text-white text-sm font-semibold rounded-full backdrop-blur-sm flex items-center gap-2">
                <i className={`${viewLabels.find((v) => v.key === active360Tab)?.icon} text-[#e18033]`} />
                {viewLabels.find((v) => v.key === active360Tab)?.label}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* ── Fade-in keyframe ─────────────────────────────────────────────────── */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
}
